"use client";

import React from "react";
import { createStore, StateCreator, StoreApi, useStore } from "zustand";
import { useShallow } from "zustand/shallow";

//

export type BaseStoreProps = Record<string, unknown>;
export type AdvancedStore<Store extends BaseStoreProps> = ReturnType<
  typeof createAdvancedStore<Store>
>;

export function createAdvancedStore<StoreProps extends BaseStoreProps>(providerName: string) {
  type Store = StoreApi<StoreProps>;
  const Context = React.createContext<Store | null>(null);

  //

  function Provider({
    children,
    value,
  }: {
    children?: React.ReactNode;
    value: StateCreator<StoreProps> | StoreProps;
  }) {
    const storeRef = React.useRef<Store | undefined>(undefined);

    React.useEffect(() => {
      if (storeRef.current) {
        const { setState, getState } = storeRef.current;
        const v = typeof value === "function" ? value(setState, getState, storeRef.current) : value;
        storeRef.current.setState(v);
      }
    }, [value]);

    if (!storeRef.current) {
      storeRef.current = createStore<StoreProps>()((set, get, store) =>
        typeof value === "function" ? value(set, get, store) : value,
      );
    }

    return <Context.Provider value={storeRef.current}>{children}</Context.Provider>;
  }

  //

  function useContext<State extends StoreProps, T = State>(
    selector?: (state: State) => T,
    options: { useShallow?: boolean } = {},
  ): T {
    const store = React.useContext(Context);
    if (!store) throw new Error(`Missing ${providerName}Provider in the tree`);

    const storeSelector: (state: StoreProps) => T = (state) => {
      if (selector === undefined) return state as unknown as T;
      return selector(state as State);
    };

    const shallowedSelector = useShallow(storeSelector);

    return useStore(store, options.useShallow ? shallowedSelector : storeSelector);
  }

  //

  function useNullableContext<State extends StoreProps, T = State>(
    selector?: (state: State) => T,
    options: { useShallow?: boolean } = {},
  ): T | null {
    try {
      return useContext(selector, options);
    } catch {
      return null;
    }
  }

  //

  return { Provider, useContext, useNullableContext };
}
